import {
  Button,
  CloseButton,
  Flex,
  Heading,
  Modal,
  Spinner,
  Text,
} from "@instructure/ui";
import { t } from "i18next";
import React, { useEffect, useState } from "react";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import CustomRTE, { EMPTY_RTE } from "../CustomTextArea/CustomRTE";

const ReflectionModal = ({
  openModal,
  setOpenModal,
  reflection,
  handleSubmit,
  handleSubmitFromTemplate,
  onDraftChange,
  onDraftChangeFromTemplate,
  contextName,
  draftEnabled,
  sectionRefTemplate,
}) => {
  const showNotification = useNotification();
  const [savingAsDraft, setSavingAsDraft] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [reflectionText, setReflectionText] = useState(
    reflection?.content || EMPTY_RTE
  );
  const [sections, setSections] = useState(
    reflection?.sections ||
      sectionRefTemplate?.sections?.map((section) => ({
        ...section,
        content: EMPTY_RTE,
      }))
  );

  useEffect(() => {
    if (sectionRefTemplate && !reflection) {
      setSections(
        sectionRefTemplate.sections.map((section) => ({
          ...section,
          content: EMPTY_RTE,
        }))
      );
    }
  }, [sectionRefTemplate, reflection]);

  async function savingDraft() {
    if (hasFilledOutReflection()) {
      setSavingAsDraft(true);
      const templateId = reflection?.template_id ?? sectionRefTemplate?.id;
      if (templateId) {
        await onDraftChangeFromTemplate(templateId, sections);
      } else {
        await onDraftChange(reflectionText, reflection);
      }
      setSavingAsDraft(false);
    }
  }

  async function publishingReflection() {
    if (hasFilledOutReflection()) {
      setPublishing(true);
      const templateId = reflection?.template_id ?? sectionRefTemplate?.id;
      if (templateId) {
        await handleSubmitFromTemplate(templateId, sections);
      } else {
        await handleSubmit(reflectionText);
      }
      setPublishing(false);
    }
  }

  function hasFilledOutReflection() {
    if (sectionRefTemplate) {
      if (sections.some((section) => section.content === EMPTY_RTE)) {
        showNotification(t("reflections.submitTemplateReflectionError"), ERROR);
        return false;
      } else {
        return true;
      }
    } else {
      if (reflectionText === EMPTY_RTE) {
        showNotification(t("reflections.submitReflectionError"), ERROR);
        return false;
      } else {
        return true;
      }
    }
  }

  function handleAnswerQuestion(value, sectionId) {
    setSections((prevSections) =>
      prevSections.map((section) =>
        section.id === sectionId ? { ...section, content: value } : section
      )
    );
  }

  return (
    <Modal
      open={openModal}
      size="medium"
      label={t("reflections.reflectionText")}
      onExited={() => {
        setReflectionText(reflection?.content || "");
        setSections(
          reflection?.sections ||
            sectionRefTemplate?.sections?.map((section) => ({
              ...section,
              content: EMPTY_RTE,
            }))
        );
      }}
    >
      <Modal.Header>
        <Flex justifyItems="space-between">
          <Flex.Item>
            <Heading as="h3">{t("reflections.reflectionText")}</Heading>
          </Flex.Item>
          <Flex.Item>
            <CloseButton
              offset="small"
              onClick={() => setOpenModal(false)}
              screenReaderLabel={t("global.close")}
            />
          </Flex.Item>
        </Flex>
      </Modal.Header>
      <Modal.Body>
        <Flex wrap="wrap" gap="medium">
          <Flex.Item width="100%">
            <Text weight="bold">{t("reflections.relatesTo")} </Text>
            <Text weight="bold" color="brand">
              {contextName}
            </Text>
          </Flex.Item>
          <Flex.Item width="100%">
            {reflection?.from_template ||
            (!reflection && sectionRefTemplate) ? (
              <Flex wrap="wrap" gap="large">
                {sections?.map((section) => (
                  <Flex.Item key={section.id} width="100%">
                    <Flex wrap="wrap" gap="small">
                      <Flex.Item width="100%">
                        <Text weight="bold">{section.header_question}</Text>
                      </Flex.Item>
                      {section.helper_text &&
                        section.helper_text !== EMPTY_RTE && (
                          <Flex.Item>
                            <CustomRTE
                              id={`helper-text-${section.id}`}
                              defaultValue={section.helper_text}
                              readOnly
                            />
                          </Flex.Item>
                        )}
                      <Flex.Item>
                        <CustomRTE
                          id={`reflection-text-${section.id}`}
                          onChange={(value) =>
                            handleAnswerQuestion(value, section.id)
                          }
                          defaultValue={section.content}
                        />
                      </Flex.Item>
                    </Flex>
                  </Flex.Item>
                ))}
              </Flex>
            ) : (
              <CustomRTE
                id="reflection-text"
                defaultValue={reflectionText}
                onChange={(value) => setReflectionText(value)}
              />
            )}
          </Flex.Item>
        </Flex>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={() => setOpenModal(false)} margin="0 x-small 0 0">
          {t("global.cancel")}
        </Button>
        {draftEnabled && (
          <Button
            margin="0 x-small 0 0"
            onClick={savingDraft}
            color="secondary"
            disabled={savingAsDraft}
          >
            {savingAsDraft ? (
              <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
            ) : (
              t("finalizeCourse.saveAsPrivate")
            )}
          </Button>
        )}
        <Button
          onClick={publishingReflection}
          color="primary"
          type="submit"
          disabled={publishing}
        >
          {publishing ? (
            <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
          ) : (
            t("reflections.publish")
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ReflectionModal;
